import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MessageCircleMore,
  Download,
  Loader2,
  Eye,
  Upload,
  Users,
  LayoutGrid,
  Armchair,
  Printer,
} from 'lucide-react';
import { NewGuestModal } from '@/components/Events/NewGuestModal';
import { EditGuestModal } from '@/components/Events/EditGuestModal';
import { WhatsAppInviteModal } from '@/components/Events/WhatsAppInviteModal';
import { GuestMessageHistoryModal } from '@/components/Events/GuestMessageHistoryModal';
import { ImportGuestsModal } from '@/components/Events/ImportGuestsModal';
import { MesasDistribucion } from '@/components/Events/Mesas/MesasDistribucion';
import { MesasAsignaciones } from '@/components/Events/Mesas/MesasAsignaciones';
import { eventService } from '@/services/eventService';
import { mesaService } from '@/services/mesaService';
import { Guest, Mesa } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { useEventPermission } from '@/hooks/useEventPermission';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchEvent } from '@/store/eventSlice';
import { buildGuestCsv, downloadCsv } from '@/lib/guestCsv';

// Define types that match the modal components' expected types
interface WhatsAppGuest {
  id: string;
  nombre: string;
  tel: string;
  estado: string;
}

interface EditModalGuest {
  id: string;
  nombre: string;
  tel: string;
  estado: string;
  fecha_respuesta: string | null;
}

export const EventGuestManagement: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [showNewGuestModal, setShowNewGuestModal] = useState(false);
  const [showEditGuestModal, setShowEditGuestModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showMessageHistoryModal, setShowMessageHistoryModal] = useState(false);
  const [showImportGuestsModal, setShowImportGuestsModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<EditModalGuest | null>(null);
  const [selectedGuestForMessages, setSelectedGuestForMessages] = useState<Guest | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();
  const { canWrite, role, isOwner } = useEventPermission(eventId);
  const showReadOnlyBadge = !isOwner && role === 'READ_ONLY';

  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const { selectedEvent, loading: eventLoading } = useAppSelector(state => state.event);

  // Load event, guests, and mesas on component mount
  useEffect(() => {
    if (eventId) {
      dispatch(fetchEvent(eventId));
      loadGuestsAndMesas();
    }
  }, [eventId, dispatch]);

  const loadGuestsAndMesas = async () => {
    if (!eventId) return;

    try {
      setLoading(true);
      const [guestsData, mesasData] = await Promise.all([
        eventService.getEventGuests(eventId),
        mesaService.getEventMesas(eventId),
      ]);
      setGuests(guestsData);
      setMesas(mesasData);
    } catch (error) {
      console.error('Error loading guests/mesas data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPrint = () => {
    if (!eventId) return;
    window.open(`/app/events/${eventId}/mesas/imprimir`, '_blank', 'noopener');
  };

  const handleCreateGuest = async (guestData: Omit<Guest, 'id' | 'event_id'>) => {
    if (!eventId) return;

    try {
      const newGuest = await eventService.createGuest(guestData, eventId);
      setGuests(prev => [...prev, newGuest]);
      setShowNewGuestModal(false);
      toast({
        title: 'Éxito',
        description: 'Invitado agregado correctamente'
      });
    } catch (error) {
      console.error('Error creating guest:', error);
      toast({
        title: 'Error',
        description: 'No se pudo agregar el invitado',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateGuest = async (guestId: string, guestData: Partial<Omit<Guest, 'id' | 'event_id'>>) => {
    try {
      const updatedGuest = await eventService.updateGuest(guestId, guestData);
      setGuests(prev => prev.map(g => g.id === guestId ? updatedGuest : g));
      setShowEditGuestModal(false);
      setSelectedGuest(null);
      toast({
        title: 'Éxito',
        description: 'Invitado actualizado correctamente'
      });
    } catch (error) {
      console.error('Error updating guest:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el invitado',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateAcompanantes = async (guestId: string, next: number) => {
    try {
      const updated = await eventService.updateGuest(guestId, { numero_acompanantes: next });
      setGuests(prev => prev.map(g => g.id === guestId ? updated : g));
    } catch (error) {
      console.error('Error updating acompañantes:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el número de acompañantes',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteGuest = async (guestId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este invitado?')) {
      return;
    }

    try {
      await eventService.deleteGuest(guestId);
      setGuests(prev => prev.filter(g => g.id !== guestId));
      toast({
        title: 'Éxito',
        description: 'Invitado eliminado correctamente'
      });
    } catch (error) {
      console.error('Error deleting guest:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el invitado',
        variant: 'destructive'
      });
    }
  };

  const handleImportGuests = async (
    parsedGuests: Array<Omit<Guest, 'id' | 'event_id'>>,
    onProgress: (inserted: number, total: number) => void,
  ) => {
    if (!eventId) return;

    try {
      const inserted = await eventService.bulkCreateGuests(parsedGuests, eventId, {
        onProgress,
      });
      setGuests(prev => [...prev, ...inserted]);
      toast({
        title: 'Éxito',
        description: `Se importaron ${inserted.length} invitado(s) correctamente`,
      });
    } catch (error) {
      console.error('Error importing guests:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron importar los invitados',
        variant: 'destructive',
      });
      throw error; // let the modal know not to close
    }
  };

  const handleExportGuests = () => {
    if (!selectedEvent) return;
    // Sanitize event name for filename: lowercase, alphanumerics + dashes.
    const slug = selectedEvent.nombre
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'evento';
    const filename = `invitados-${slug}.csv`;
    downloadCsv(filename, buildGuestCsv(guests));
    toast({
      title: 'Lista exportada',
      description: guests.length === 0
        ? 'Se descargó la plantilla vacía.'
        : `Se exportaron ${guests.length} invitado(s).`,
    });
  };

  const getStatusBadge = (estado: string) => {
    switch (estado) {
      case 'CONFIRMADO':
        return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Confirmado</Badge>;
      case 'PENDIENTE':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pendiente</Badge>;
      case 'RECHAZADO':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rechazado</Badge>;
      default:
        return <Badge variant="outline">{estado}</Badge>;
    }
  };

  const handleEditGuest = (guest: Guest) => {
    // Convert Guest to EditModalGuest
    const editGuest: EditModalGuest = {
      id: guest.id,
      nombre: guest.nombre,
      tel: guest.tel,
      estado: guest.estado,
      fecha_respuesta: guest.fecha_respuesta ? guest.fecha_respuesta.toISOString() : null
    };
    setSelectedGuest(editGuest);
    setShowEditGuestModal(true);
  };

  const handleViewMessageHistory = (guest: Guest) => {
    setSelectedGuestForMessages(guest);
    setShowMessageHistoryModal(true);
  };

  const formatDate = (date?: Date) => {
    if (!date) return '-';
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter guests based on search term
  const filteredGuests = guests.filter(guest =>
    guest.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    guest.tel.includes(searchTerm)
  );

  // Calculate statistics
  const confirmedCount = guests.filter(g => g.estado === 'CONFIRMADO').length;
  const pendingCount = guests.filter(g => g.estado === 'PENDIENTE').length;
  const declinedCount = guests.filter(g => g.estado === 'RECHAZADO').length;

  // Convert guests to the format expected by WhatsAppInviteModal
  const whatsAppGuests: WhatsAppGuest[] = guests.map(guest => ({
    id: guest.id,
    nombre: guest.nombre,
    tel: guest.tel,
    estado: guest.estado
  }));

  if (loading || eventLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900">Evento no encontrado</h2>
        <p className="text-gray-500 mt-2">El evento que buscas no existe o no tienes acceso a él.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex  xs:flex-col sm:flex-row justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            {selectedEvent.nombre}
            {showReadOnlyBadge && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Solo lectura</Badge>
            )}
          </h1>
          <p className="text-gray-600 mt-1">
            {formatDate(selectedEvent.fecha)} • Gestión de Invitados
          </p>
        </div>
        <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
          <Button variant="whatsapp" onClick={() => setShowWhatsAppModal(true)} disabled={!canWrite}>
            <MessageCircleMore className="w-4 h-4 mr-2" />
            Enviar Invitaciones
          </Button>
          <Button variant="outline" onClick={() => setShowImportGuestsModal(true)} disabled={!canWrite}>
            <Upload className="w-4 h-4 mr-2" />
            Importar CSV
          </Button>
          <Button variant="outline" onClick={handleOpenPrint} disabled={mesas.length === 0}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimir mesas
          </Button>
          <Button onClick={() => setShowNewGuestModal(true)} disabled={!canWrite}>
            <Plus className="w-4 h-4 mr-2" />
            Agregar Invitado
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de invitados</p>
                <p className="text-2xl font-bold text-gray-900">{guests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Confirmaron</p>
                <p className="text-2xl font-bold text-green-600">{confirmedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pendientes</p>
                <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rechazaron</p>
                <p className="text-2xl font-bold text-red-600">{declinedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Lista | Distribución | Asignación */}
      <Tabs defaultValue="lista">
        <TabsList className="h-auto bg-slate-100 p-1 gap-1 flex-wrap justify-start">
          <TabsTrigger
            value="lista"
            className="gap-2 px-4 py-2 text-slate-600 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-colors"
          >
            <Users className="w-4 h-4" />
            Lista de invitados
          </TabsTrigger>
          <TabsTrigger
            value="distribucion"
            className="gap-2 px-4 py-2 text-slate-600 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-colors"
          >
            <LayoutGrid className="w-4 h-4" />
            Distribución de mesas
          </TabsTrigger>
          <TabsTrigger
            value="asignacion"
            className="gap-2 px-4 py-2 text-slate-600 data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-white/60 transition-colors"
          >
            <Armchair className="w-4 h-4" />
            Asignación de asientos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lista" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <CardTitle>Lista de Invitados</CardTitle>
                <div className="flex items-center space-x-2 mt-5 sm:mt-0">
                  <Search className="w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar invitados..."
                    className="w-55 sm:w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button variant="outline" size="sm" onClick={handleExportGuests}>
                    <Download className="w-4 h-4 mr-1" />
                    Exportar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredGuests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {guests.length === 0 ? 'No hay invitados agregados' : 'No se encontraron invitados con ese criterio'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Teléfono</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="w-32">Acompañantes</TableHead>
                      <TableHead>Fecha de respuesta</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGuests.map((guest) => (
                      <TableRow key={guest.id}>
                        <TableCell className="font-medium">{guest.nombre}</TableCell>
                        <TableCell>{guest.tel}</TableCell>
                        <TableCell>{getStatusBadge(guest.estado)}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            max={20}
                            value={guest.numero_acompanantes}
                            className="h-8 w-20"
                            disabled={!canWrite}
                            onChange={(e) => {
                              const next = Math.max(0, Math.min(20, Number(e.target.value) || 0));
                              if (next === guest.numero_acompanantes) return;
                              handleUpdateAcompanantes(guest.id, next);
                            }}
                            aria-label={`Acompañantes de ${guest.nombre}`}
                          />
                        </TableCell>
                        <TableCell>{guest.fecha_respuesta ? formatDate(guest.fecha_respuesta) : '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewMessageHistory(guest)}
                              title="Ver historial de mensajes"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditGuest(guest)}
                              disabled={!canWrite}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteGuest(guest.id)}
                              disabled={!canWrite}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distribucion" className="mt-4">
          {eventId && (
            <MesasDistribucion
              eventId={eventId}
              mesas={mesas}
              onMesasChange={setMesas}
              canWrite={canWrite}
            />
          )}
        </TabsContent>

        <TabsContent value="asignacion" className="mt-4">
          <MesasAsignaciones
            mesas={mesas}
            guests={guests}
            onGuestsChange={setGuests}
            canWrite={canWrite}
          />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <NewGuestModal
        open={showNewGuestModal}
        onOpenChange={setShowNewGuestModal}
        onSubmit={handleCreateGuest}
      />

      <EditGuestModal
        open={showEditGuestModal}
        onOpenChange={setShowEditGuestModal}
        guest={selectedGuest}
        onSave={(editedGuest) => {
          // Convert EditModalGuest back to Guest for the update
          const guestData: Partial<Omit<Guest, 'id' | 'event_id'>> = {
            nombre: editedGuest.nombre,
            tel: editedGuest.tel,
            estado: editedGuest.estado as 'PENDIENTE' | 'CONFIRMADO' | 'RECHAZADO',
          };
          handleUpdateGuest(editedGuest.id, guestData);
        }}
      />

      <WhatsAppInviteModal
        open={showWhatsAppModal}
        onOpenChange={setShowWhatsAppModal}
        event={selectedEvent}
        guests={whatsAppGuests}
      />

      <ImportGuestsModal
        open={showImportGuestsModal}
        onOpenChange={setShowImportGuestsModal}
        onImport={handleImportGuests}
      />

      {selectedGuestForMessages && eventId && (
        <GuestMessageHistoryModal
          open={showMessageHistoryModal}
          onOpenChange={setShowMessageHistoryModal}
          guestId={selectedGuestForMessages.id}
          guestName={selectedGuestForMessages.nombre}
          guestPhone={selectedGuestForMessages.tel}
          eventId={eventId}
        />
      )}
    </div>
  );
};
